import { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Upload, 
  Inbox, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  Download,
  Clock,
  CheckCircle,
  AlertCircle,
  LayoutDashboard,
  Settings,
  Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import FinancialDashboard from './FinancialDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Document type configuration based on checklist items
const CHECKLIST_CONFIG = {
  core: {
    label: 'Core Documents (All Clients)',
    items: [
      { id: 'onboarding_form', label: 'Completed Oakmere Client Onboarding Form', required: true },
      { id: 'photo_id', label: 'Government-issued photo ID (passport or driving licence)', required: true },
      { id: 'proof_of_address', label: 'Proof of address dated within the last 3 months', required: true },
      { id: 'business_bank_statements', label: 'Business bank statements — full 12 months, all accounts', required: true },
      { id: 'personal_bank_statements', label: 'Personal bank statements — full 12 months', required: true },
      { id: 'previous_accounts', label: "Previous year's filed accounts or tax return", required: false },
      { id: 'hmrc_correspondence', label: 'Any HMRC correspondence received during the year', required: false },
      { id: 'loan_details', label: 'Details of business loans, finance, or credit facilities', required: false },
      { id: 'asset_details', label: 'Details of assets purchased or disposed of this year', required: false },
      { id: 'personal_money_introduced', label: 'Details of personal money introduced into the business', required: false }
    ]
  },
  ecommerce: {
    label: 'E-Commerce',
    items: [
      { id: 'platform_sales_report', label: 'Full year sales report from every platform (Shopify, Amazon, Etsy etc.)', required: true },
      { id: 'payout_reports', label: 'Payout / disbursement reports from each platform', required: true },
      { id: 'payment_processor_statements', label: 'Payment processor statements — Stripe, PayPal, Square, Klarna etc.', required: true },
      { id: 'supplier_invoices', label: 'Supplier invoices for all stock purchases', required: true },
      { id: 'stock_values', label: 'Opening and closing stock value', required: true },
      { id: 'fba_fee_reports', label: 'Amazon FBA fee reports and fulfilment cost statements', required: false },
      { id: 'shipping_costs', label: 'Shipping and postage costs not shown in platform reports', required: false },
      { id: 'advertising_spend', label: 'Advertising spend reports — Meta Ads, Google Ads, TikTok Ads', required: false },
      { id: 'platform_subscriptions', label: 'Platform subscription and fee summaries', required: false }
    ]
  },
  vat: {
    label: 'Tax (If Registered)',
    items: [
      { id: 'vat_returns', label: 'Tax returns submitted for all quarters during the year', required: true },
      { id: 'import_vat_certificates', label: 'Import tax certificates (C79) if importing goods', required: false },
      { id: 'vat_purchase_invoices', label: 'All purchase invoices showing tax from UK suppliers', required: true }
    ]
  },
  crypto: {
    label: 'Crypto & Digital Assets',
    items: [
      { id: 'exchange_transaction_history', label: 'Full transaction history export from every exchange used', required: true },
      { id: 'wallet_addresses', label: 'All wallet addresses used (hot wallets and cold wallets)', required: true },
      { id: 'defi_transaction_history', label: 'DeFi protocol transaction history', required: true },
      { id: 'crypto_purchase_records', label: 'Purchase records for all crypto — date, amount, GBP value', required: true },
      { id: 'crypto_payment_records', label: 'Records of crypto received as payment', required: true },
      { id: 'nft_records', label: 'NFT purchase and sale records', required: false },
      { id: 'staking_rewards', label: 'Staking and yield farming reward records', required: false },
      { id: 'airdrop_records', label: 'Airdrop and hard fork records', required: false },
      { id: 'wallet_transfers', label: 'Records of crypto transferred between your own wallets', required: false },
      { id: 'crypto_spend_records', label: 'Records of crypto used to purchase goods or services', required: false }
    ]
  },
  creator: {
    label: 'Content Creators & Digital Entrepreneurs',
    items: [
      { id: 'youtube_adsense', label: 'YouTube AdSense annual payment history', required: true },
      { id: 'tiktok_earnings', label: 'TikTok Creator Fund or TikTok Shop earnings statements', required: true },
      { id: 'patreon_earnings', label: 'Patreon, Ko-fi, Buy Me a Coffee earnings', required: true },
      { id: 'onlyfans_earnings', label: 'OnlyFans or subscription platform earnings statement', required: true },
      { id: 'brand_deal_invoices', label: 'All invoices raised for brand deals or sponsored content', required: true },
      { id: 'affiliate_earnings', label: 'Affiliate marketing earnings reports', required: true },
      { id: 'payment_platform_history', label: 'PayPal, Wise, or Revolut transaction history — full 12 months', required: true },
      { id: 'equipment_receipts', label: 'Equipment receipts (camera, lighting, microphone)', required: false },
      { id: 'software_subscriptions', label: 'Editing software subscriptions (Adobe, Final Cut etc.)', required: false },
      { id: 'studio_rental', label: 'Studio rental or filming location hire invoices', required: false },
      { id: 'travel_expenses', label: 'Travel costs to brand events, shoots, or projects', required: false },
      { id: 'management_fees', label: 'Management fees, agent commission, or talent agency invoices', required: false }
    ]
  }
};

const BUSINESS_TYPES = [
  { id: 'ecommerce', label: 'E-Commerce', description: 'Shopify, Amazon, Etsy, TikTok Shop, WooCommerce' },
  { id: 'crypto', label: 'Crypto & Digital Assets', description: 'Traders, DeFi, NFTs, staking, Web3' },
  { id: 'creator', label: 'Content Creator', description: 'YouTube, TikTok, OnlyFans, Patreon, podcasts' }
];

// Sidebar Navigation
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const navItems = [
    { path: '/portal', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/portal/upload', icon: Upload, label: 'Upload Documents' },
    { path: '/portal/my-documents', icon: FileText, label: 'My Documents' },
    { path: '/portal/inbox', icon: Inbox, label: 'Inbox' },
    { path: '/portal/settings', icon: Settings, label: 'Settings' }
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const isActive = (path, exact) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-obsidian/50 z-40 lg:hidden" 
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-iron-grey z-50 transform transition-transform duration-200 ease-out lg:translate-x-0 ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-iron-grey">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-[14px] font-bold text-obsidian tracking-widest" style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
                OAKMERE ADVISORY
              </Link>
              <button onClick={onClose} className="lg:hidden text-iron-grey hover:text-obsidian">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>
            <p className="font-inter text-xs text-iron-grey mt-1">Client Portal</p>
          </div>
          
          {/* User Info */}
          <div className="p-4 border-b border-iron-grey bg-parchment">
            <p className="font-inter text-sm font-medium text-obsidian truncate">{user?.name}</p>
            <p className="font-inter text-xs text-iron-grey truncate">{user?.email}</p>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-1">
              {navItems.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={onClose}
                    className={`flex items-center gap-3 px-4 py-3 font-inter text-sm transition-colors ${
                      isActive(item.path, item.exact)
                        ? 'bg-obsidian text-parchment'
                        : 'text-obsidian hover:bg-parchment'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <item.icon size={18} strokeWidth={1.5} />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
          
          {/* Logout */}
          <div className="p-4 border-t border-iron-grey">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full font-inter text-sm text-iron-grey hover:text-obsidian hover:bg-parchment transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={18} strokeWidth={1.5} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Get document types based on business types
const getDocumentTypes = (businessTypes = []) => {
  const types = [];
  
  // Always add core documents
  CHECKLIST_CONFIG.core.items.forEach(item => {
    types.push({ value: item.id, label: item.label });
  });
  
  // Add VAT (common for all business types)
  CHECKLIST_CONFIG.vat.items.forEach(item => {
    types.push({ value: item.id, label: item.label });
  });
  
  // Add based on business types
  if (businessTypes.includes('ecommerce')) {
    CHECKLIST_CONFIG.ecommerce.items.forEach(item => {
      types.push({ value: item.id, label: item.label });
    });
  }
  
  if (businessTypes.includes('crypto')) {
    CHECKLIST_CONFIG.crypto.items.forEach(item => {
      types.push({ value: item.id, label: item.label });
    });
  }
  
  if (businessTypes.includes('creator')) {
    CHECKLIST_CONFIG.creator.items.forEach(item => {
      types.push({ value: item.id, label: item.label });
    });
  }
  
  // Add a general/other option
  types.push({ value: 'other', label: 'Other document' });
  
  return types;
};

// Upload Documents Page
const UploadDocuments = () => {
  const { user, getAuthHeader } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  
  const businessTypes = user?.business_types || [];
  const documentTypes = getDocumentTypes(businessTypes);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    if (!documentType) {
      toast.error('Please select a document type');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Document uploaded successfully');
      setSelectedFile(null);
      setDocumentType('');
      // Reset file input
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Get visible checklist sections based on business types
  const getVisibleSections = () => {
    const sections = ['core', 'vat']; // Always show core and VAT
    if (businessTypes.includes('ecommerce')) sections.push('ecommerce');
    if (businessTypes.includes('crypto')) sections.push('crypto');
    if (businessTypes.includes('creator')) sections.push('creator');
    return sections;
  };

  const visibleSections = getVisibleSections();

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-6">Upload Documents</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upload Section */}
        <div className="bg-white border border-iron-grey p-6 card-shadow">
          <p className="font-inter text-sm text-iron-grey mb-6">
            Upload your financial documents securely. Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG.
          </p>
          
          {/* File Drop Zone */}
          <div className="border-2 border-dashed border-iron-grey p-8 text-center mb-6 hover:border-obsidian transition-colors">
            <input
              type="file"
              id="file-input"
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              data-testid="file-input"
            />
            <label 
              htmlFor="file-input" 
              className="cursor-pointer"
            >
              <Upload size={32} className="mx-auto mb-4 text-iron-grey" strokeWidth={1.5} />
              <p className="font-inter text-sm text-obsidian mb-1">
                {selectedFile ? selectedFile.name : 'Click to select a file'}
              </p>
              <p className="font-inter text-xs text-iron-grey">
                {selectedFile 
                  ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                  : 'or drag and drop here'}
              </p>
            </label>
          </div>
          
          {/* Document Type */}
          <div className="mb-6">
            <label className="font-inter text-sm text-obsidian mb-2 block">What document is this?</label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger className="bg-parchment border-iron-grey" data-testid="document-type-select">
                <SelectValue placeholder="Select document type..." />
              </SelectTrigger>
              <SelectContent className="bg-white border-iron-grey max-h-80">
                {documentTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value} className="text-sm">
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Upload Button */}
          <Button 
            onClick={handleUpload}
            disabled={!selectedFile || !documentType || uploading}
            className="btn-primary w-full"
            data-testid="upload-btn"
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </Button>
          
          {businessTypes.length === 0 && (
            <p className="font-inter text-xs text-iron-grey mt-4 p-3 bg-parchment border-l-2 border-obsidian">
              <strong>Tip:</strong> Go to <Link to="/portal/settings" className="underline">Settings</Link> to select your business type and see a personalized checklist.
            </p>
          )}
        </div>
        
        {/* Checklist Section */}
        <div className="bg-white border border-iron-grey p-6 card-shadow max-h-[80vh] overflow-y-auto">
          <h2 className="font-fraunces font-bold text-lg text-obsidian mb-2">What To Send</h2>
          <p className="font-inter text-xs text-iron-grey mb-4">
            Items marked <span className="font-medium text-obsidian">Required</span> are essential — we cannot proceed without them.
          </p>
          
          {visibleSections.map((sectionKey) => {
            const section = CHECKLIST_CONFIG[sectionKey];
            return (
              <div key={sectionKey} className="mb-6">
                <h3 className="font-inter text-sm font-medium text-obsidian uppercase tracking-wider mb-3 pb-2 border-b border-iron-grey">
                  {section.label}
                </h3>
                <ul className="space-y-2">
                  {section.items.map((item) => (
                    <li key={item.id} className="flex items-start gap-3">
                      <div className="w-4 h-4 border border-iron-grey flex-shrink-0 mt-0.5" />
                      <span className="font-inter text-sm text-obsidian">
                        {item.label}
                        {item.required && (
                          <span className="ml-2 text-xs font-medium text-obsidian bg-parchment px-1.5 py-0.5">Required</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          
          <p className="font-inter text-xs text-iron-grey mt-4 pt-4 border-t border-iron-grey">
            If unsure whether something is relevant — upload it anyway. Contact your Oakmere account manager if you have questions.
          </p>
        </div>
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = () => {
  const { user, updateBusinessTypes } = useAuth();
  const [saving, setSaving] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState(user?.business_types || []);

  const toggleBusinessType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId)
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateBusinessTypes(selectedTypes);
      toast.success('Settings updated successfully');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(selectedTypes.sort()) !== JSON.stringify((user?.business_types || []).sort());

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-6">Settings</h1>
      
      <div className="bg-white border border-iron-grey p-6 card-shadow max-w-xl">
        <h2 className="font-fraunces font-bold text-lg text-obsidian mb-2">Business Type</h2>
        <p className="font-inter text-sm text-iron-grey mb-6">
          Select the type(s) of business you run. This determines which documents appear in your upload checklist.
        </p>
        
        <div className="space-y-3 mb-6">
          {BUSINESS_TYPES.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => toggleBusinessType(type.id)}
              className={`w-full p-4 border text-left transition-all ${
                selectedTypes.includes(type.id)
                  ? 'border-obsidian bg-obsidian text-parchment'
                  : 'border-iron-grey bg-parchment text-obsidian hover:border-obsidian'
              }`}
              data-testid={`settings-business-type-${type.id}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-inter text-sm font-medium">{type.label}</p>
                  <p className={`font-inter text-xs ${
                    selectedTypes.includes(type.id) ? 'text-parchment/70' : 'text-iron-grey'
                  }`}>
                    {type.description}
                  </p>
                </div>
                {selectedTypes.includes(type.id) && (
                  <Check size={18} strokeWidth={2} />
                )}
              </div>
            </button>
          ))}
        </div>
        
        <Button 
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="btn-primary w-full"
          data-testid="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        
        {!hasChanges && selectedTypes.length > 0 && (
          <p className="font-inter text-xs text-iron-grey mt-4 text-center">
            No changes to save
          </p>
        )}
      </div>
    </div>
  );
};

// My Documents Page
const MyDocuments = () => {
  const { getAuthHeader } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents/my-uploads`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processed':
        return <CheckCircle size={16} className="text-green-600" strokeWidth={1.5} />;
      case 'pending':
        return <Clock size={16} className="text-iron-grey" strokeWidth={1.5} />;
      default:
        return <AlertCircle size={16} className="text-iron-grey" strokeWidth={1.5} />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  // Format document type for display
  const formatDocumentType = (docType) => {
    // Find the label from checklist config
    for (const section of Object.values(CHECKLIST_CONFIG)) {
      const item = section.items.find(i => i.id === docType);
      if (item) return item.label;
    }
    // Fallback to formatted string
    return docType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-6">My Documents</h1>
      
      {documents.length === 0 ? (
        <div className="bg-white border border-iron-grey p-8 text-center card-shadow">
          <FileText size={48} className="mx-auto mb-4 text-iron-grey" strokeWidth={1} />
          <p className="font-inter text-obsidian mb-2">No documents uploaded yet</p>
          <p className="font-inter text-sm text-iron-grey">
            Upload your first document to get started
          </p>
          <Link to="/portal/upload" className="btn-primary inline-block mt-4">
            Upload Document
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-iron-grey card-shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="documents-table">
              <thead>
                <tr className="border-b border-iron-grey bg-parchment">
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Document</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Type</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Size</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Date</th>
                  <th className="px-4 py-3 text-left font-inter text-xs uppercase tracking-wider text-iron-grey">Status</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.id} className="border-b border-iron-grey last:border-0 hover:bg-parchment/50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-iron-grey" strokeWidth={1.5} />
                        <span className="font-inter text-sm text-obsidian truncate max-w-xs">
                          {doc.original_filename}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-sm text-iron-grey">
                        {formatDocumentType(doc.document_type)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-sm text-iron-grey">
                        {formatFileSize(doc.file_size)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-inter text-sm text-iron-grey">
                        {formatDate(doc.uploaded_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(doc.status)}
                        <span className="font-inter text-sm text-obsidian capitalize">
                          {doc.status}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Inbox Page
const InboxPage = () => {
  const { getAuthHeader } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInbox = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/documents/inbox`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/inbox/${doc.id}/download`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="font-inter text-iron-grey">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-fraunces font-bold text-2xl text-obsidian mb-2">Inbox</h1>
      <p className="font-inter text-sm text-iron-grey mb-6">
        Processed documents from your accountant
      </p>
      
      {documents.length === 0 ? (
        <div className="bg-white border border-iron-grey p-8 text-center card-shadow">
          <Inbox size={48} className="mx-auto mb-4 text-iron-grey" strokeWidth={1} />
          <p className="font-inter text-obsidian mb-2">Your inbox is empty</p>
          <p className="font-inter text-sm text-iron-grey">
            Processed documents will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {documents.map((doc) => (
            <div 
              key={doc.id} 
              className="bg-white border border-iron-grey p-4 card-shadow hover:shadow-card-hover transition-shadow"
              data-testid={`inbox-doc-${doc.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <FileText size={24} className="text-obsidian flex-shrink-0" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="font-inter text-sm font-medium text-obsidian truncate">
                      {doc.original_filename}
                    </p>
                    <p className="font-inter text-xs text-iron-grey mt-1">
                      Received {formatDate(doc.sent_at)}
                    </p>
                    {doc.message && (
                      <p className="font-inter text-sm text-graphite mt-2 bg-parchment p-2 border-l-2 border-obsidian">
                        {doc.message}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => handleDownload(doc)}
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 border-obsidian text-obsidian hover:bg-obsidian hover:text-parchment"
                  data-testid={`download-${doc.id}`}
                >
                  <Download size={16} strokeWidth={1.5} className="mr-2" />
                  Download
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Client Portal Component
const ClientPortal = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-parchment">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-iron-grey px-4 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-obsidian"
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} strokeWidth={1.5} />
            </button>
            <div className="lg:hidden font-fraunces font-bold text-lg text-obsidian">
              Client Portal
            </div>
            <div className="w-6 lg:hidden" /> {/* Spacer */}
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-4 lg:p-8" data-testid="client-portal-content">
          <Routes>
            <Route index element={<FinancialDashboard />} />
            <Route path="upload" element={<UploadDocuments />} />
            <Route path="my-documents" element={<MyDocuments />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default ClientPortal;
